/**
 * What a failed call is, on this side of the wire.
 *
 * `core-api` answers `{code, message}` with an English code and a French
 * message (`core_api/internal/errors.py`). The rule here is that the French
 * message is displayed as-is and the code is never shown: the server owns the
 * words the family reads, so there is exactly one place where they are written.
 */

export type ErrorCode =
  | "not_authenticated"
  | "invalid_credentials"
  | "device_not_trusted"
  | "pin_locked"
  | "device_locked"
  | "forbidden"
  | "not_found"
  | "validation_failed"
  | "offline"
  | "network"
  | "unexpected";

export class ApiError extends Error {
  readonly status: number;
  readonly code: ErrorCode | string;
  readonly retryAfterSeconds: number | null;

  constructor(
    status: number,
    code: ErrorCode | string,
    message: string,
    retryAfterSeconds: number | null = null,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.retryAfterSeconds = retryAfterSeconds;
  }

  /** Worth putting in the outbox rather than showing as a failure. */
  get isTransport(): boolean {
    return this.code === "offline" || this.code === "network" || this.status >= 500;
  }

  get needsLogin(): boolean {
    return this.status === 401;
  }
}

/**
 * The child shell asked for a route that names a child.
 *
 * Invariant 2 of the specification, enforced at run time rather than by review.
 * It is a programming error, not a user-facing one — which is why it is not an
 * `ApiError` and carries no French message.
 */
export class PartitionViolation extends Error {
  constructor(url: string) {
    super(
      `La coquille élève a tenté d'appeler ${url}. ` +
        "Aucune route appelée par un enfant ne porte d'identifiant d'enfant (web-pwa §14.2).",
    );
    this.name = "PartitionViolation";
  }
}

export function offlineError(): ApiError {
  return new ApiError(0, "offline", "Pas de réseau pour le moment.");
}
